import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { DoorType } from '../../../constants/enum';

/**
 * Base class cho tất cả loại tủ - Updated để support Module system với optimized methods
 */
export class BaseCabinet {
  constructor(type, sceneManager) {
    this.type = type;
    this.sceneManager = sceneManager;
    this.modules = new Map(); // Map<string, Module> - key là module.id
    this.id = `${type}_${Date.now()}`;

    this.meshPoints = []; // Lưu trữ mesh points cho edge detection

    this.doorType = DoorType.COVER; // Mặc định là cửa phủ bì
    this.beveled = false; // Mặc định là không vát cạnh 45

    this.cabinetGroup = []; // Lưu trữ tất cả groups của cabinet
    // Cabinet-level properties
    this.isVisible = true;
    this.isLocked = false;
    this.metadata = {};
  }

  countModules() {
    return this.modules.size;
  }

  /**
   * Abstract method - phải được override bởi subclass
   * Trả về constraints cho loại tủ này
   */
  getConstraints() {
    throw new Error('getConstraints() must be implemented by subclass');
  }

  /**
   * Abstract method - phải được override bởi subclass
   * Tạo module instance phù hợp với loại tủ
   */
  createModule(moduleData, moduleType) {
    throw new Error('createModule() must be implemented by subclass');
  }

  addPlanToCheck(model) {
    if (!model) return;
    this.sceneManager.planToCheck.push(model);

    // model.traverse((child) => {
    //   if (child.isMesh && child.name.includes("HAU")) {
    //     this.sceneManager.planToCheck.push(child);
    //   }
    // });
  }

  /**
   * Thêm module mới vào cabinet
   */
  async addModule(moduleData, moduleType, yPosition) {
    try {
      // Tạo module instance từ subclass
      const module = this.createModule(moduleData, moduleType);

      // Load 3D model
      await module.load(yPosition);

      // Thêm metadata cho cabinet
      if (module.group) {
        module.group.userData.cabinetType = this.type;
        module.group.userData.moduleType = moduleType;
        module.group.userData.cabinetId = this.id;
        module.group.userData.moduleId = module.id;
        // Id bền cho từng instance, dùng làm neo cho DIM (được lưu/khôi phục cùng thiết kế)
        module.group.userData.instanceId = uuidv4();
        this.cabinetGroup.push(module.group);
        this.addPlanToCheck(module.group);
      }

      // Lưu vào Map
      this.modules.set(module.id, module);

      // Call event hook
      this.onModuleAdded(module);

      return module;
    } catch (error) {
      console.error(`Error adding module to ${this.type} cabinet:`, error);
      throw error;
    }
  }

  getAllMeshPoints() {
    const all = [];
    for (const module of this.modules.values()) {
      all.push(...module.getMeshPoints());
    }
    return all;
  }

  setMeshPoints() {
    this.meshPoints = [];
    if (this.cabinetType === 'kitchen') {
      this.meshPoints = this.currentCabinet.getActiveMeshPoints();
    } else if (this.cabinetType === 'wardrobe') {
      this.meshPoints = this.getAllMeshPoints();
    }
  }

  changeBevelebCabinet(beveled) {
    if (beveled !== null && beveled !== undefined) {
      this.beveled = beveled;
    }
  }

  /**
   * Xóa module theo ID
   */
  removeModule(moduleId) {
    for (const [key, module] of this.modules) {
      if (module.group.uuid === moduleId) {
        module.group.traverse(o => {
          o.raycast = () => {}; // chặn raycast
          if (o.isMesh) {
            o.geometry?.dispose?.();
            const mat = o.material;
            if (Array.isArray(mat)) mat.forEach(m => m?.dispose?.());
            else mat?.dispose?.();
          }
        });
        this.sceneManager.scene.remove(module.group);
        this.sceneManager.scene?.updateMatrixWorld(true);
        if (module) {
          module.dispose();
          this.modules.delete(key);
          this.onModuleRemoved(module);
          return true;
        }
        console.warn(`Module ${moduleId} not found in ${this.type} cabinet`);
        return false;
      }
    }
  }

  /**
   * Xóa module theo reference
   */
  removeModuleByReference(moduleGroup) {
    const moduleId = moduleGroup.userData.moduleId;
    if (moduleId) {
      return this.removeModule(moduleId);
    }

    // Fallback: tìm theo THREE.Group reference
    for (const [id, module] of this.modules) {
      if (module.group === moduleGroup) {
        return this.removeModule(id);
      }
    }
    return false;
  }

  // ===== OPTIMIZED RETRIEVAL METHODS =====

  /**
   * OPTIMIZED: Lấy module theo ID trực tiếp từ Map - O(1)
   */
  getModule(moduleId) {
    return this.modules.get(moduleId);
  }

  /**
   * OPTIMIZED: Alias cho getModule để consistency với CabinetManager
   */
  getModuleById(moduleId) {
    return this.modules.get(moduleId) || null;
  }

  /**
   * OPTIMIZED: Tìm module theo group UUID trực tiếp từ Map
   */
  getModuleByGroupUuid(groupUuid) {
    // Iterate through Map values directly - more efficient than converting to array first
    for (const module of this.modules.values()) {
      if (module.group && module.group.uuid === groupUuid) {
        return module;
      }
    }
    return null;
  }

  /**
   * OPTIMIZED: Lấy modules theo type với hiệu suất cao
   */
  getModulesByType(moduleType) {
    const result = [];

    // Direct iteration over Map values - more efficient than Array.from().filter()
    for (const module of this.modules.values()) {
      // if (
      //   module.moduleType === moduleType ||
      //   module.cabinetType === moduleType ||
      //   (module.type && module.type === moduleType)
      // ) {
      //   result.push(module);
      // }
      result.push(module);
    }

    return result;
  }

  /**
   * Lấy tất cả modules
   */
  getAllModules() {
    return Array.from(this.modules.values());
  }

  /**
   * NEW: Lấy modules trong khu vực cụ thể
   */
  getModulesInArea(boundingBox) {
    const result = [];

    for (const module of this.modules.values()) {
      if (module.position && boundingBox.containsPoint(module.position)) {
        result.push(module);
      }
    }

    return result;
  }

  /**
   * NEW: Tìm kiếm modules theo nhiều tiêu chí
   */
  searchModules(criteria = {}) {
    const result = [];

    for (const module of this.modules.values()) {
      // Tìm theo type
      if (
        criteria.type &&
        module.moduleType !== criteria.type &&
        module.cabinetType !== criteria.type
      ) {
        continue;
      }

      // Tìm theo material
      if (criteria.material && module.material !== criteria.material) {
        continue;
      }

      // Tìm theo color
      if (criteria.color && module.color !== criteria.color) {
        continue;
      }

      // Tìm theo trạng thái loaded
      if (
        criteria.loaded !== undefined &&
        module.isLoaded !== criteria.loaded
      ) {
        continue;
      }

      // Tìm theo trạng thái visible
      if (
        criteria.visible !== undefined &&
        module.isVisible !== criteria.visible
      ) {
        continue;
      }

      // Tìm theo kích thước tối thiểu
      if (
        criteria.minWidth &&
        (!module.actualSize || module.actualSize.x < criteria.minWidth)
      ) {
        continue;
      }

      // Nếu pass tất cả criteria
      result.push(module);
    }

    return result;
  }

  // ===== COUNT AND STATISTICS METHODS =====

  /**
   * Lấy số lượng modules
   */
  getModuleCount() {
    return this.modules.size;
  }

  /**
   * OPTIMIZED: Đếm modules theo type với hiệu suất cao
   */
  getModuleCountByType(moduleType = null) {
    if (moduleType) {
      // Count specific type
      let count = 0;
      for (const module of this.modules.values()) {
        if (
          module.moduleType === moduleType ||
          module.cabinetType === moduleType ||
          (module.type && module.type === moduleType)
        ) {
          count++;
        }
      }
      return count;
    }

    // Return distribution of all types
    return this.getModuleTypeDistribution();
  }

  /**
   * OPTIMIZED: Lấy phân bố modules theo type
   */
  getModuleTypeDistribution() {
    const distribution = {};

    for (const module of this.modules.values()) {
      const type = module.moduleType || module.cabinetType || 'unknown';
      distribution[type] = (distribution[type] || 0) + 1;
    }

    return distribution;
  }

  /**
   * Kiểm tra module có tồn tại không
   */
  hasModule(moduleId) {
    return this.modules.has(moduleId);
  }

  /**
   * NEW: Kiểm tra module có tồn tại theo group UUID
   */
  hasModuleByGroupUuid(groupUuid) {
    for (const module of this.modules.values()) {
      if (module.group && module.group.uuid === groupUuid) {
        return true;
      }
    }
    return false;
  }

  /**
   * NEW: Lấy tất cả group UUIDs của modules
   */
  getAllGroupUuids() {
    const uuids = [];

    for (const module of this.modules.values()) {
      if (module.group && module.group.uuid) {
        uuids.push(module.group.uuid);
      }
    }

    return uuids;
  }

  // ===== POSITION AND SPATIAL METHODS =====

  /**
   * OPTIMIZED: Tìm module gần vị trí nhất
   */
  findModuleAtPosition(position, maxDistance = 1) {
    let closestModule = null;
    let minDistance = maxDistance;

    for (const module of this.modules.values()) {
      if (!module.position) continue;

      const distance = module.position.distanceTo(position);
      if (distance < minDistance) {
        minDistance = distance;
        closestModule = module;
      }
    }

    return closestModule;
  }

  /**
   * Lấy bounding box của toàn bộ cabinet
   */
  getBoundingBox() {
    const box = new THREE.Box3();

    this.modules.forEach(module => {
      if (module.group && module.isVisible) {
        const moduleBox = new THREE.Box3().setFromObject(module.group);
        box.union(moduleBox);
      }
    });

    return box;
  }

  /**
   * Lấy center point của cabinet
   */
  getCenter() {
    const box = this.getBoundingBox();
    return box.getCenter(new THREE.Vector3());
  }

  // ===== MODULE MANAGEMENT METHODS =====

  /**
   * Xóa tất cả modules
   */
  clearAllModules() {
    this.modules.forEach(module => {
      module.dispose();
    });
    this.modules.clear();

    this.onAllModulesCleared();
  }

  /**
   * Hiện/ẩn toàn bộ cabinet
   */
  setVisible(visible) {
    this.isVisible = visible;
    this.modules.forEach(module => {
      module.setVisible(visible);
    });
  }

  /**
   * Lock/unlock cabinet (ngăn thay đổi)
   */
  setLocked(locked) {
    this.isLocked = locked;
  }

  /**
   * Update tất cả modules (ví dụ: sau khi thay đổi constraints)
   */
  updateAllModules() {
    this.modules.forEach(module => {
      // Subclass có thể override method này để custom update logic
      this.updateModule(module);
    });
  }

  /**
   * Update một module cụ thể - có thể override
   */
  updateModule(module) {
    // Default implementation - subclass có thể override
  }

  /**
   * Sắp xếp lại vị trí modules - có thể override
   */
  repositionModules() {
    // Group modules by type
    const modulesByType = {};
    this.modules.forEach(module => {
      const type = module.moduleType;
      if (!modulesByType[type]) {
        modulesByType[type] = [];
      }
      modulesByType[type].push(module);
    });

    // Reposition each type - subclass sẽ implement logic cụ thể
    Object.keys(modulesByType).forEach(type => {
      this.repositionModulesByType(type, modulesByType[type]);
    });
  }

  /**
   * Sắp xếp lại modules của một type - abstract method
   */
  repositionModulesByType(moduleType, modules) {
    // Subclass sẽ implement logic cụ thể
  }

  // ===== SERIALIZATION METHODS =====

  /**
   * Serialize cabinet data để save/load
   */
  serialize() {
    const modulesData = [];
    this.modules.forEach(module => {
      modulesData.push(module.getData());
    });

    return {
      id: this.id,
      type: this.type,
      isVisible: this.isVisible,
      isLocked: this.isLocked,
      metadata: this.metadata,
      constraints: this.constraints,
      modules: modulesData,
      createdAt: Date.now(),
    };
  }

  /**
   * Load cabinet từ serialized data
   */
  async deserialize(data) {
    this.id = data.id;
    this.isVisible = data.isVisible;
    this.isLocked = data.isLocked;
    this.metadata = data.metadata || {};

    // Clear existing modules
    this.clearAllModules();

    // Load modules
    for (const moduleData of data.modules) {
      try {
        const module = await this.deserializeModule(moduleData);
        this.modules.set(module.id, module);
      } catch (error) {
        console.error(`Error loading module from data:`, error);
      }
    }
  }

  /**
   * Abstract method - deserialize một module
   */
  async deserializeModule(moduleData) {
    throw new Error('deserializeModule() must be implemented by subclass');
  }

  // ===== EVENT HOOKS =====

  /**
   * Event hooks - có thể override trong subclass
   */
  onModuleAdded(module) {
    // Override trong subclass nếu cần
  }

  onModuleRemoved(module) {
    // Override trong subclass nếu cần
    // Có thể trigger reposition các modules khác
  }

  onAllModulesCleared() {
    // Override trong subclass nếu cần
  }

  // ===== VALIDATION AND DEBUG METHODS =====

  /**
   * Validate cabinet state
   */
  validate() {
    const errors = [];

    // Check basic properties
    if (!this.type) errors.push('Cabinet type is required');
    if (!this.sceneManager) errors.push('SceneManager is required');

    // Check modules
    this.modules.forEach((module, id) => {
      if (module.id !== id) {
        errors.push(`Module ID mismatch: ${module.id} !== ${id}`);
      }
      if (!module.isLoaded) {
        errors.push(`Module ${id} is not loaded`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  /**
   * Debug info
   */
  getDebugInfo() {
    return {
      id: this.id,
      type: this.type,
      moduleCount: this.modules.size,
      modulesByType: this.getModuleTypeDistribution(),
      isVisible: this.isVisible,
      isLocked: this.isLocked,
      boundingBox: this.getBoundingBox(),
      center: this.getCenter(),
    };
  }
}
